<template>
  <div class="text-center">
    <v-dialog v-model="dialog" width="800">
      <template v-slot:activator="{ on, attrs }">
        <slot name="activator" :on="on" :attrs="attrs">
          <v-btn color="primary" v-bind="attrs" v-on="on">
            Start our Guide on Double Opt-In
          </v-btn>
        </slot>
      </template>

      <v-card class="elevation-2">
        <v-stepper v-model="step">
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1" @click="step = 1" class="clickable-step">
              What is it?
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 2" step="2" @click="step = 2" class="clickable-step">
              Advantages
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step step="3" @click="step = 3" class="clickable-step">
              Default Settings
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1">
              <v-card-text>
                <v-alert type="info" outlined class="mb-4">
                  <strong>Disclaimer:</strong> This is not legal advice. Contact an attorney if you have legal or
                  compliance
                  questions.
                </v-alert>
                <h4 class="text-subtitle-1 font-weight-bold d-flex align-center mb-2">
                  <v-icon left color="info">mdi-information-outline</v-icon>
                  What is Double Opt-In?
                </h4>
                <p class="mb-4">
                  Double opt-in is a two-step process used to confirm a person’s consent to receive text messages.
                  First, the
                  individual provides their phone number, typically through a web form or by texting a keyword. Next,
                  they
                  must confirm their intent to subscribe by replying “YES” to a verification SMS.
                </p>
                <p class="mb-4">
                  For example, with a double opt-in process using a website lightbox, a user might initially enter their
                  name,
                  phone number, and email address on your site.
                </p>
                <div class="text-center my-4">
                  <img alt="Double opt-in example 1" src="@/assets/double-opt-in-1.png"
                    style="max-width: 100%; max-height: 400px; border-radius: 6px;" class="elevation-2" />
                </div>
                <p class="mb-4">
                  After submitting the form, the consumer will automatically receive a text message requesting that they
                  reply
                  “YES” to confirm their consent. The user is only subscribed once they complete this <em>second</em>
                  step.
                  For example:
                </p>
                <div class="text-center my-4">
                  <img alt="Double opt-in example 2" src="@/assets/double-opt-in-2.png"
                    style="max-width: 100%; max-height: 400px;border-radius: 6px;" class="elevation-2" />
                </div>
              </v-card-text>
              <div class="pa-4 d-flex justify-end">
                <v-btn text @click="dialog = false">Cancel</v-btn>
                <v-btn color="primary" @click="step = 2" class="ml-2">
                  Continue
                </v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-card-text>
                <v-alert type="info" outlined class="mb-4">
                  <strong>Disclaimer:</strong> This is not legal advice. Contact an attorney if you have legal or
                  compliance
                  questions.
                </v-alert>
                <h4 class="text-subtitle-1 font-weight-bold d-flex align-center mb-2">
                  <v-icon left color="success">mdi-shield-check</v-icon>
                  Advantages
                </h4>
                <p class="mb-4">
                  Double opt-in has some possible advantages. It can create a higher quality subscriber list because
                  your
                  subscribers had to take two positive actions to subscribe — provide their number and reply YES.
                </p>
                <p>
                  Double opt-in may also avoid the risk of sending messages to the wrong number (e.g. typos) or someone
                  claiming that they never provided you with a telephone number. </p>

                <h4 class="text-subtitle-1 font-weight-bold d-flex align-center mb-2">
                  <v-icon left color="primary">mdi-lightbulb-on-outline</v-icon>
                  How RedChirp helps to mitigate some of the drawbacks of Double Opt-In
                </h4>
                <p class="mb-4">
                  Poorly implemented double opt-in procedures can have some significant drawbacks.
                </p>
                <p class="mb-4">
                  Between texting a customer a double opt-in request and when the customer replies “YES” they should be
                  treated as having opted out, similar to someone who texts the “STOP” keyword. During this time period,
                  RedChirp will show the recipient as having “Opted-out pending double opt-in”
                </p>
                <p class="mb-4">
                  Because there will always be a certain percentage of consumers who start — but do not finish — the
                  double
                  opt-in process, it is often best for automated processes to be setup in a such a way as to avoid
                  sending
                  unnecessary double opt-in requests.
                </p>
                <p class="mb-4">
                  For example, imagine that you create a form to record interest in attending an upcoming winemaker's
                  dinner
                  and configure that form to send double opt-in request -- it might be a good idea to avoid sending
                  those
                  double opt-in requests to individuals have previously completed the double opt-in process using the
                  same
                  phone number. </p>
                <p class="mb-4">
                  By default, RedChirp's automated processes will not send double opt-in requests to recipients who, for
                  the
                  same provided phone number, already have consent on file in RedChirp.
                </p>
              </v-card-text>
              <div class="pa-4 d-flex justify-end">
                <v-btn text @click="step = 1">Back</v-btn>
                <v-btn color="primary" @click="step = 3" class="ml-2">
                  Continue
                </v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-content step="3">
              <v-card-text>
                <v-alert type="info" outlined class="mb-4">
                  <strong>Disclaimer:</strong> This is not legal advice. Contact an attorney if you have legal or
                  compliance
                  questions.
                </v-alert>
                <h4 class="text-subtitle-1 font-weight-bold d-flex align-center mb-2">
                  <v-icon left color="primary">mdi-cog-outline</v-icon>
                  Default Settings
                </h4>
                <p class="mb-4">
                  <strong>RedChirp Default:</strong>
                </p>
                <ul class="mb-4">
                  <li><strong>Automated processes should initially opt new recipients out when starting the double opt-in process: </strong> Enabled</li>
                  <li><strong>Automated processes should not send double opt-in request when the recipient already has consent on file in RedChirp: </strong> Enabled</li>
                </ul>


              </v-card-text>
              <div class="pa-4 d-flex align-center justify-space-between">
                <div class="caption" v-if="org_settings_link">
                  <v-icon small left color="primary">mdi-cog-outline</v-icon>
                  <a :href="org_settings_link" target="_blank">Open Organization General Settings (Double Opt-In)</a>
                </div>
                <div class="d-flex justify-end">
                  <v-btn text @click="step = 2">Back</v-btn>
                  <v-btn color="primary" @click="dialog = false" class="ml-2">
                    Finish
                  </v-btn>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'DoubleOptInTradeoffsStepper',
  props: {
    organization_id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      dialog: false, // Controls the visibility of the dialog
      step: 1,       // Controls the current step in the stepper
    };
  },
  computed: {
    org_settings_link() {
      const org_id = this.organization_id || (this.$route && this.$route.params && this.$route.params.organization_id);
      if (!org_id) return null;
      return this.$router.resolve({
        name: 'settings-organization',
        params: { organization_id: org_id },
        query: { open_panel: 0 },
      }).href;
    },
  },
  methods: {
    goToOrgSettings() {
      if (!this.org_settings_link) return;
      window.open(this.org_settings_link, '_blank');
    },
  },
  watch: {
    // This watcher resets the stepper to the first step every time the dialog is opened.
    dialog(isOpen) {
      if (isOpen) {
        this.step = 1;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__text p {
  line-height: 1.75;
}

.clickable-step {
  cursor: pointer;
}
</style>